import * as Dialog from '@radix-ui/react-dialog';
import type { HTMLAttributes } from 'react';
import { forwardRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { ComponentProps } from '~/chords/ComponentProps';
import { getClassNameOverride } from '~/chords/getClassNameOverride';
import { IconButton } from '~/components/IconButton';
import { cn } from '~/lib/cn';

interface ModalDialogHeaderProps
  extends Omit<ComponentProps<HTMLAttributes<HTMLDivElement>>, 'className'> {
  children: ReactNode;
  showCloseButton?: boolean;
}

export const ModalDialogHeader = forwardRef<
  HTMLDivElement,
  ModalDialogHeaderProps
>(({ children, showCloseButton = true, ...props }, ref) => {
  const { t } = useTranslation('common');
  const className = getClassNameOverride(props, 'ModalDialog.Header');

  return (
    <div
      ref={ref}
      {...props}
      className={cn(
        'flex items-start justify-between gap-sm px-xl pb-sm pt-lg',
        className,
      )}
    >
      <Dialog.Title className="text-headline-md font-semibold leading-tight">
        {children}
      </Dialog.Title>
      {showCloseButton ? (
        <Dialog.Close asChild>
          <IconButton
            type="button"
            iconType="xmark"
            iconVariant="regular"
            size="large"
            className="-my-md -mr-lg duration-300 ease-out"
            aria-label={t('closeModal')}
          />
        </Dialog.Close>
      ) : null}
    </div>
  );
});

ModalDialogHeader.displayName = 'ModalDialog.Header';
